import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Info } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { useAuth } from "@/context/CrmAuth";
import { Loader } from "@/crm/ui";
import { cn } from "@/lib/utils";
import { AVAIL_META, AVATAR_BG, CARD, DOT, PILL, THEAD, TR, fmtDate } from "./shared";

const ROLE_LABELS = { admin: "Administrateur", manager: "Manager", commercial: "Commercial" };
const SELECT = "w-full px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15";

export default function TeamAvailability() {
  const { user } = useAuth();
  const isManager = ["admin", "manager"].includes(user?.role);
  const [team, setTeam] = useState(null);

  const load = useCallback(() => {
    api.get("/telephony/team").then(({ data }) => setTeam(data)).catch(() => setTeam([]));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const setAvailability = async (u, status) => {
    try {
      await api.put(`/telephony/team/${u.id}/availability`, { status });
      toast.success(`${u.name} → ${AVAIL_META[status]?.label}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (team === null) return <Loader />;

  return (
    <div data-testid="telephony-team">
      <div className={cn(CARD, "p-4 mb-3.5 flex gap-3")}>
        <span className="h-7 w-7 shrink-0 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <Info size={15} />
        </span>
        <p className="text-sm text-slate-500 text-pretty">
          Le routage combine le statut défini par le manager et la connexion live au CRM : un commercial non connecté est
          hors ligne, même si son statut est « Disponible ».
        </p>
      </div>

      {/* Tableau en lg+ */}
      <div className={cn(CARD, "overflow-hidden hidden lg:block")}>
        <table className="w-full text-sm table-fixed">
          <thead>
            <tr className={THEAD}>
              <th className="px-5 py-2.5 text-left">Utilisateur</th>
              <th className="px-2 py-2.5 text-left w-[132px]">Rôle</th>
              <th className="px-2 py-2.5 text-left w-[128px]">Connexion</th>
              <th className="px-2 py-2.5 text-left w-[168px]">Statut manager</th>
              <th className="px-2 py-2.5 text-left w-[148px]">Effectif routage</th>
              <th className="px-5 py-2.5 text-right w-[132px]">Activité</th>
            </tr>
          </thead>
          <tbody>
            {team.map((u, i) => (
              <tr key={u.id} data-testid={`team-row-${u.id}`} className={TR}>
                <td className="px-5 py-2.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={u.name} i={i} />
                    <span className="font-medium text-slate-800 truncate">{u.name}</span>
                  </div>
                </td>
                <td className="px-2 py-2.5 text-slate-500">{ROLE_LABELS[u.role] || u.role}</td>
                <td className="px-2 py-2.5"><Online online={u.online} /></td>
                <td className="px-2 py-2.5"><AvailSelect u={u} isManager={isManager} onChange={setAvailability} /></td>
                <td className="px-2 py-2.5"><Effective u={u} /></td>
                <td className="px-5 py-2.5 text-right font-mono text-[11.5px] text-slate-400">{fmtDate(u.last_seen_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cartes en dessous de lg — la ligne team-row-<id> existe dans les deux rendus */}
      <div className="flex flex-col gap-2.5 lg:hidden">
        {team.map((u, i) => (
          <div key={u.id} data-testid={`team-row-${u.id}`} className={cn(CARD, "p-4")}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar name={u.name} i={i} />
                <div className="min-w-0">
                  <div className="font-medium text-slate-800 truncate">{u.name}</div>
                  <div className="text-xs text-slate-400">{ROLE_LABELS[u.role] || u.role}</div>
                </div>
              </div>
              <Online online={u.online} />
            </div>
            <div className="mt-3">
              <span className="block text-xs text-slate-400 mb-1">Statut manager</span>
              <AvailSelect u={u} isManager={isManager} onChange={setAvailability} />
            </div>
            <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-100">
              <Effective u={u} />
              <span className="font-mono text-[11.5px] text-slate-400">{fmtDate(u.last_seen_at)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Avatar({ name, i }) {
  return (
    <div className={cn("h-8 w-8 shrink-0 rounded-full text-white flex items-center justify-center text-xs font-semibold", AVATAR_BG[i % AVATAR_BG.length])}>
      {(name || "?")[0]}
    </div>
  );
}

function Online({ online }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", online ? "text-emerald-700" : "text-slate-400")}>
      <span className={cn(DOT, online ? "bg-emerald-500" : "bg-slate-300")} />
      {online ? "Connecté" : "Hors ligne"}
    </span>
  );
}

function AvailSelect({ u, isManager, onChange }) {
  if (!isManager) return <span className="text-slate-600 text-sm">{AVAIL_META[u.availability]?.label}</span>;
  return (
    <select
      data-testid={`team-availability-select-${u.id}`}
      className={SELECT}
      value={u.availability}
      onChange={(e) => onChange(u, e.target.value)}
    >
      <option value="disponible">Disponible</option>
      <option value="occupe">Occupé</option>
      <option value="absent">Absent</option>
      <option value="desactive">Désactivé</option>
    </select>
  );
}

function Effective({ u }) {
  const eff = AVAIL_META[u.effective] || AVAIL_META.hors_ligne;
  return (
    <span data-testid={`team-effective-${u.id}`} className={cn(PILL, eff.bg, eff.text)}>
      <span className={cn(DOT, eff.dot)} /> {eff.label}
    </span>
  );
}
